/**
 * Shopping Cart Component
 */

import { BuilderActions } from '../../../shared'
import { IBuilderComponent } from '../../base'
import { BaseAiComponentInput } from '../../registry'
import type {
  ComponentAlignment,
  ResponsiveValue,
  SpacingInputValue,
  TypographyValue,
  VariableTextValue,
} from '../../types'
import {
  deepMerge,
  parseAlignmentFormat,
  parseSpacingFormat,
  parseToVariableText,
  parseTypographyFormat,
  parseWidthFormat,
} from '../../parser-utils'
import {
  formatFromVariableText,
  formatSpacingFormat,
  formatTypographyFormat,
  formatWidthFormat,
  formatComponentAlignmentFormat,
} from '../../format-utils'
import shoppingCartTemplate from './shopping-cart.template.json'

const SHOPPING_CART_DEFAULT_TEMPLATE = shoppingCartTemplate as unknown as IShoppingCartComponentSettings

export interface IShoppingCartComponentSettings {
  // Data settings
  'shopping-cart-data-source': string
  'shopping-cart-data-product-name': string
  'shopping-cart-data-product-image': string
  'shopping-cart-data-product-info': string
  'shopping-cart-data-product-stock-status': string
  'shopping-cart-data-product-price': string
  'shopping-cart-data-product-quantity': string
  'shopping-cart-data-comp-title': VariableTextValue
  'shopping-cart-data-filter': any // Filter object with conditions
  'shopping-cart-data-sort-type': string

  // Design settings
  'shopping-cart-design-comp-h-align': ResponsiveValue<ComponentAlignment>
  'shopping-cart-design-comp-title-align': ResponsiveValue<any>
  'shopping-cart-design-bg-color': string
  'shopping-cart-design-summary-bg-color': string
  'shopping-cart-design-primary-button-color': any
  'shopping-cart-design-secondary-button-color': any
  'shopping-cart-design-width': ResponsiveValue<string[] | number[]>
  'shopping-cart-design-image-width': ResponsiveValue<string[] | number[]>
  'shopping-cart-design-margin': ResponsiveValue<SpacingInputValue>
  'shopping-cart-design-comp-title-margin': ResponsiveValue<SpacingInputValue>
  'shopping-cart-design-corner-radius': string
  'shopping-cart-design-image-radius': any
  'shopping-cart-design-button-radius': any
  'shopping-cart-design-typography': {
    'shopping-cart-design-comp-title-typography': ResponsiveValue<TypographyValue>
    'shopping-cart-design-info-typography': ResponsiveValue<TypographyValue>
    'shopping-cart-design-title-typography': ResponsiveValue<TypographyValue>
  }

  // Actions
  'shopping-cart-action-item-click': BuilderActions
  'shopping-cart-action-qty-increment': BuilderActions
  'shopping-cart-action-qty-decrement': BuilderActions
  'shopping-cart-action-remove-item': BuilderActions
  'shopping-cart-action-on-proceed': BuilderActions
  'shopping-cart-action-on-back': BuilderActions

  // Behavior settings
  'shopping-cart-behavior-allow-qty-change': boolean
  'shopping-cart-behavior-allow-remove-item': boolean
}

export interface IShoppingCartComponent extends IBuilderComponent {
  componentId: 'shopping-cart'
  groupId: 'list'
  settings: IShoppingCartComponentSettings
}

/**
 * Shopping Cart AI Input
 */
export interface AiShoppingCartInput extends BaseAiComponentInput {
  dataSource: string // Required: Table ID
  cartTitle?: string[] // Format: ["text:Shopping Cart"] or ["text:Cart for ", "profile:username"]

  // Data columns
  productNameColumn?: string // Column ID for product name
  productImageColumn?: string // Column ID for product image
  productInfoColumn?: string // Column ID for product info
  productStockStatusColumn?: string // Column ID for stock status
  productPriceColumn?: string // Column ID for product price
  productQuantityColumn?: string // Column ID for product quantity

  // Filter & Sort
  filter?: any // Filter object (will be JSON stringified)
  sortBy?: string // Column ID to sort by
  sortType?: 'asc' | 'desc' // Sort direction

  // Styling
  width?: string // Format: "w:100%" or "w:100% de:w:60%"
  alignment?: string // Format: "center" or "left"
  titleAlignment?: string // Title alignment
  margin?: string // Format: "m:16px"
  titleMargin?: string // Title margin
  backgroundColor?: string // Background color
  summaryBackgroundColor?: string // Summary background color
  primaryButtonColor?: string // Primary button color
  secondaryButtonColor?: string // Secondary button color
  cornerRadius?: string // Corner radius
  imageRadius?: string // Image radius
  buttonRadius?: string // Button radius
  imageWidth?: string // Image width format: "w:80px"

  // Typography
  typography?: {
    title?: string // Title typography
    info?: string // Info typography
    compTitle?: string // Component title typography
  }

  // Actions
  actionItemClick?: BuilderActions
  actionQtyIncrement?: BuilderActions
  actionQtyDecrement?: BuilderActions
  actionRemoveItem?: BuilderActions
  actionOnProceed?: BuilderActions
  actionOnBack?: BuilderActions

  // Behavior
  allowQuantityChange?: boolean // Allow quantity change
  allowRemoveItem?: boolean // Allow remove item
}

export class ShoppingCartComp {
  getTemplate(): IShoppingCartComponentSettings {
    return SHOPPING_CART_DEFAULT_TEMPLATE
  }

  /**
   * format shopping cart component settings to AI shopping cart input
   */
  format(settings: IShoppingCartComponentSettings): Omit<AiShoppingCartInput, 'type'> {
    const result: Omit<AiShoppingCartInput, 'type'> = {
      dataSource: settings['shopping-cart-data-source'] || '',
    }

    // Data - Source (required)
    if (settings['shopping-cart-data-source']) {
      result.dataSource = settings['shopping-cart-data-source']
    }

    // Data - Columns
    if (settings['shopping-cart-data-product-name']) {
      result.productNameColumn = settings['shopping-cart-data-product-name']
    }

    if (settings['shopping-cart-data-product-image']) {
      result.productImageColumn = settings['shopping-cart-data-product-image']
    }

    if (settings['shopping-cart-data-product-info']) {
      result.productInfoColumn = settings['shopping-cart-data-product-info']
    }

    if (settings['shopping-cart-data-product-stock-status']) {
      result.productStockStatusColumn = settings['shopping-cart-data-product-stock-status']
    }

    if (settings['shopping-cart-data-product-price']) {
      result.productPriceColumn = settings['shopping-cart-data-product-price']
    }

    if (settings['shopping-cart-data-product-quantity']) {
      result.productQuantityColumn = settings['shopping-cart-data-product-quantity']
    }

    // Data - Component Title
    if (settings['shopping-cart-data-comp-title']) {
      result.cartTitle = formatFromVariableText(settings['shopping-cart-data-comp-title'])
    }

    // Data - Filter
    if (settings['shopping-cart-data-filter']) {
      result.filter = settings['shopping-cart-data-filter']
    }

    // Data - Sort
    if (settings['shopping-cart-data-sort-type']) {
      result.sortType = settings['shopping-cart-data-sort-type'] as 'asc' | 'desc'
    }

    // Design - Width
    if (settings['shopping-cart-design-width']) {
      result.width = formatWidthFormat(settings['shopping-cart-design-width'])
    }

    // Design - Image Width
    if (settings['shopping-cart-design-image-width']) {
      result.imageWidth = formatWidthFormat(settings['shopping-cart-design-image-width'])
    }

    // Design - Alignment
    if (settings['shopping-cart-design-comp-h-align']) {
      result.alignment = formatComponentAlignmentFormat(settings['shopping-cart-design-comp-h-align'])?.replace(/^h:/, '') || undefined
    }

    // Design - Title Alignment
    if (settings['shopping-cart-design-comp-title-align']) {
      const allSame = Object.values(settings['shopping-cart-design-comp-title-align']).every((v) => v === Object.values(settings['shopping-cart-design-comp-title-align'])[0])
      if (allSame) {
        result.titleAlignment = String(Object.values(settings['shopping-cart-design-comp-title-align'])[0])
      } else {
        result.titleAlignment = formatComponentAlignmentFormat(settings['shopping-cart-design-comp-title-align'] as any)?.replace(/^h:/, '') || undefined
      }
    }

    // Design - Margin
    if (settings['shopping-cart-design-margin']) {
      result.margin = formatSpacingFormat(settings['shopping-cart-design-margin'])
    }

    // Design - Title Margin
    if (settings['shopping-cart-design-comp-title-margin']) {
      result.titleMargin = formatSpacingFormat(settings['shopping-cart-design-comp-title-margin'])
    }

    // Design - Colors
    if (settings['shopping-cart-design-bg-color']) {
      result.backgroundColor = settings['shopping-cart-design-bg-color']
    }

    if (settings['shopping-cart-design-summary-bg-color']) {
      result.summaryBackgroundColor = settings['shopping-cart-design-summary-bg-color']
    }

    if (settings['shopping-cart-design-primary-button-color']) {
      result.primaryButtonColor = settings['shopping-cart-design-primary-button-color'] as any
    }

    if (settings['shopping-cart-design-secondary-button-color']) {
      result.secondaryButtonColor = settings['shopping-cart-design-secondary-button-color'] as any
    }

    // Design - Corner Radius
    if (settings['shopping-cart-design-corner-radius']) {
      result.cornerRadius = settings['shopping-cart-design-corner-radius']
    }

    if (settings['shopping-cart-design-image-radius']) {
      result.imageRadius = settings['shopping-cart-design-image-radius'] as any
    }

    if (settings['shopping-cart-design-button-radius']) {
      result.buttonRadius = settings['shopping-cart-design-button-radius'] as any
    }

    // Design - Typography
    if (settings['shopping-cart-design-typography']) {
      result.typography = {}
      if (settings['shopping-cart-design-typography']['shopping-cart-design-title-typography']) {
        result.typography.title = formatTypographyFormat(settings['shopping-cart-design-typography']['shopping-cart-design-title-typography'])
      }
      if (settings['shopping-cart-design-typography']['shopping-cart-design-info-typography']) {
        result.typography.info = formatTypographyFormat(settings['shopping-cart-design-typography']['shopping-cart-design-info-typography'])
      }
      if (settings['shopping-cart-design-typography']['shopping-cart-design-comp-title-typography']) {
        result.typography.compTitle = formatTypographyFormat(settings['shopping-cart-design-typography']['shopping-cart-design-comp-title-typography'])
      }
    }

    // Behavior
    if (settings['shopping-cart-behavior-allow-qty-change'] !== undefined) {
      result.allowQuantityChange = settings['shopping-cart-behavior-allow-qty-change']
    }

    if (settings['shopping-cart-behavior-allow-remove-item'] !== undefined) {
      result.allowRemoveItem = settings['shopping-cart-behavior-allow-remove-item']
    }

    return result
  }

  /**
   * parse AI shopping cart input to shopping cart component settings
   */
  parse(input: AiShoppingCartInput): IShoppingCartComponentSettings {
    const result = deepMerge(SHOPPING_CART_DEFAULT_TEMPLATE, {})

    // Data - Source (required)
    if (input.dataSource) {
      result['shopping-cart-data-source'] = input.dataSource
    }

    // Data - Columns
    if (input.productNameColumn) {
      result['shopping-cart-data-product-name'] = input.productNameColumn
    }

    if (input.productImageColumn) {
      result['shopping-cart-data-product-image'] = input.productImageColumn
    }

    if (input.productInfoColumn !== undefined) {
      result['shopping-cart-data-product-info'] = input.productInfoColumn
    }

    if (input.productStockStatusColumn !== undefined) {
      result['shopping-cart-data-product-stock-status'] = input.productStockStatusColumn
    }

    if (input.productPriceColumn) {
      result['shopping-cart-data-product-price'] = input.productPriceColumn
    }

    if (input.productQuantityColumn) {
      result['shopping-cart-data-product-quantity'] = input.productQuantityColumn
    }

    // Data - Component Title
    if (input.cartTitle) {
      result['shopping-cart-data-comp-title'] = parseToVariableText(input.cartTitle)
    }

    // TODO: Data - Filter (JSON stringify as requested)
    if (input.filter !== undefined) {
      result['shopping-cart-data-filter'] = typeof input.filter === 'string' ? JSON.parse(input.filter) : input.filter
    }

    // Data - Sort
    if (input.sortType) {
      result['shopping-cart-data-sort-type'] = input.sortType
    }

    // Design - Width
    if (input.width) {
      result['shopping-cart-design-width'] = parseWidthFormat(input.width)
    }

    // Design - Image Width
    if (input.imageWidth) {
      result['shopping-cart-design-image-width'] = parseWidthFormat(input.imageWidth)
    }

    // Design - Alignment
    if (input.alignment) {
      const alignmentParsed = parseAlignmentFormat(input.alignment)
      if (alignmentParsed.hAlign) {
        result['shopping-cart-design-comp-h-align'] = alignmentParsed.hAlign
      }
    }

    if (input.titleAlignment) {
      const titleAlignParsed = parseAlignmentFormat(input.titleAlignment)
      if (titleAlignParsed.hAlign) {
        result['shopping-cart-design-comp-title-align'] = titleAlignParsed.hAlign
      }
    }

    // Design - Margin
    if (input.margin) {
      result['shopping-cart-design-margin'] = parseSpacingFormat(input.margin)
    }

    if (input.titleMargin) {
      result['shopping-cart-design-comp-title-margin'] = parseSpacingFormat(input.titleMargin)
    }

    // Design - Colors
    if (input.backgroundColor) {
      result['shopping-cart-design-bg-color'] = input.backgroundColor
    }

    if (input.summaryBackgroundColor) {
      result['shopping-cart-design-summary-bg-color'] = input.summaryBackgroundColor
    }

    if (input.primaryButtonColor) {
      result['shopping-cart-design-primary-button-color'] = input.primaryButtonColor
    }

    if (input.secondaryButtonColor) {
      result['shopping-cart-design-secondary-button-color'] = input.secondaryButtonColor
    }

    // Design - Radius
    if (input.cornerRadius) {
      result['shopping-cart-design-corner-radius'] = input.cornerRadius
    }

    if (input.imageRadius) {
      result['shopping-cart-design-image-radius'] = input.imageRadius
    }

    if (input.buttonRadius) {
      result['shopping-cart-design-button-radius'] = input.buttonRadius
    }

    // Design - Typography
    if (input.typography) {
      if (input.typography.title) {
        const titleTypo = parseTypographyFormat(input.typography.title)
        result['shopping-cart-design-typography']['shopping-cart-design-title-typography'] = titleTypo
      }

      if (input.typography.info) {
        const infoTypo = parseTypographyFormat(input.typography.info)
        result['shopping-cart-design-typography']['shopping-cart-design-info-typography'] = infoTypo
      }

      if (input.typography.compTitle) {
        const compTitleTypo = parseTypographyFormat(input.typography.compTitle)
        result['shopping-cart-design-typography']['shopping-cart-design-comp-title-typography'] = compTitleTypo
      }
    }

    // TODO:Actions
    if (input.actionItemClick) {
      result['shopping-cart-action-item-click'] = input.actionItemClick
    }

    if (input.actionQtyIncrement) {
      result['shopping-cart-action-qty-increment'] = input.actionQtyIncrement
    }

    if (input.actionQtyDecrement) {
      result['shopping-cart-action-qty-decrement'] = input.actionQtyDecrement
    }

    if (input.actionRemoveItem) {
      result['shopping-cart-action-remove-item'] = input.actionRemoveItem
    }

    // Behavior
    if (input.allowQuantityChange !== undefined) {
      result['shopping-cart-behavior-allow-qty-change'] = input.allowQuantityChange
    }

    if (input.allowRemoveItem !== undefined) {
      result['shopping-cart-behavior-allow-remove-item'] = input.allowRemoveItem
    }

    return result
  }
}
